import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MapPin, Home as HomeIcon, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { services } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookAppointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    propertyType: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings`, formData);
      
      if (response.data) {
        setSubmitted(true);
        toast.success('Booking request submitted successfully! Check your email for confirmation.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again or call us at 204-294-6802.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-brand-blue-500 shadow-lg">
            <CardContent className="p-12 text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Thank You!</h2>
              <p className="text-lg text-gray-600">
                Your booking request has been received. We'll contact you within 24 hours to confirm your appointment.
              </p>
              <div className="bg-brand-blue-50 p-6 rounded-lg space-y-2">
                <p className="text-sm text-gray-700">
                  <strong>Reference:</strong> #{Date.now()}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Service:</strong> {formData.serviceType}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Preferred Date:</strong> {formData.preferredDate}
                </p>
              </div>
              <div className="pt-4">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      serviceType: '',
                      propertyType: '',
                      address: '',
                      preferredDate: '',
                      preferredTime: '',
                      message: ''
                    });
                  }}
                  variant="outline"
                  className="mr-4"
                >
                  Book Another Appointment
                </Button>
                <a href="/">
                  <Button className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white">
                    Back to Home
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-blue-50 via-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Book Your Appointment
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get your free quote and schedule your cleaning service today
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">Request a Quote</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <User className="h-5 w-5 text-brand-blue-600" />
                    <span>Personal Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="204-XXX-XXXX"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Service Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <HomeIcon className="h-5 w-5 text-brand-blue-600" />
                    <span>Service Details</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">Service Type *</Label>
                      <Select value={formData.serviceType} onValueChange={(value) => handleChange('serviceType', value)} required>
                        <SelectTrigger id="serviceType">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.title}>
                              {service.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select value={formData.propertyType} onValueChange={(value) => handleChange('propertyType', value)} required>
                        <SelectTrigger id="propertyType">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="apartment">Apartment</SelectItem>
                          <SelectItem value="house">House</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="office">Office</SelectItem>
                          <SelectItem value="retail">Retail Space</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Property Address *</Label>
                    <Input
                      id="address"
                      placeholder="123 Main Street, Winnipeg, MB"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Schedule */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-brand-blue-600" />
                    <span>Preferred Schedule</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="preferredDate">Preferred Date *</Label>
                      <Input
                        id="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={(e) => handleChange('preferredDate', e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferredTime">Preferred Time *</Label>
                      <Select value={formData.preferredTime} onValueChange={(value) => handleChange('preferredTime', value)} required>
                        <SelectTrigger id="preferredTime">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">Morning (8:00 AM - 12:00 PM)</SelectItem>
                          <SelectItem value="afternoon">Afternoon (12:00 PM - 4:00 PM)</SelectItem>
                          <SelectItem value="evening">Evening (4:00 PM - 6:00 PM)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <MessageSquare className="h-5 w-5 text-brand-blue-600" />
                    <span>Additional Information</span>
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="message">Special Requests or Questions</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about any specific cleaning needs, access instructions, or questions you have..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-extrabold text-2xl py-10 shadow-2xl hover:shadow-3xl transform hover:scale-[1.03] transition-all duration-300 border-4 border-orange-700 rounded-xl"
                  disabled={isSubmitting}
                  style={{ backgroundColor: isSubmitting ? '#d97706' : '#f97316' }}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center text-xl">
                      ⏳ Submitting Your Request...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      🚀 SUBMIT BOOKING REQUEST 🚀
                    </span>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting this form, you agree to be contacted by Sparkle Property Care Ltd regarding your request.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Contact Alternative */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">Prefer to speak with us directly?</p>
            <a href="tel:2042946802">
              <Button size="lg" variant="outline" className="border-2 border-brand-blue-600 text-brand-blue-600 hover:bg-brand-blue-50">
                <Phone className="mr-2 h-5 w-5" />
                Call 204-294-6802
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookAppointment;
